import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import SecondaryNav from '@/components/SecondaryNav';
import api from '@/lib/api';
import { todayIsoBrt } from '@/lib/dateBrt';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { readLastProjects, dismissProject, clearLastProjects } from '@/lib/lastProject';
import {
  FolderOpen, CheckSquare, Clock, Folders, CircleCheck,
  PauseCircle, AlertTriangle, ArrowRight, X, RotateCcw,
} from 'lucide-react';

// ---------------- helpers ----------------
const GREEN = '#0F6E56';

const greetingFor = (hour) => {
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
};

const formatLongDate = (d = new Date()) => {
  const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${days[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
};

const formatTaskBadge = (task, todayIso) => {
  if (task.overdue) return { label: 'Urgente', cls: 'bg-red-100 text-red-700 border-red-200', accent: 'border-l-red-500' };
  if (task.deadline === todayIso) return { label: 'Hoje', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', accent: 'border-l-emerald-500' };
  const p = (task.priority || '').toLowerCase();
  if (p === 'high' || p === 'urgent') return { label: 'Urgente', cls: 'bg-red-100 text-red-700 border-red-200', accent: 'border-l-red-500' };
  const name = (task.name || '').toLowerCase();
  if (name.includes('reuni')) return { label: 'Reunião', cls: 'bg-blue-100 text-blue-700 border-blue-200', accent: 'border-l-blue-500' };
  if (name.includes('protocol') || name.includes('seguim')) return { label: 'Protocolo', cls: 'bg-amber-100 text-amber-700 border-amber-200', accent: 'border-l-amber-500' };
  return { label: 'Tarefa', cls: 'bg-gray-100 text-gray-700 border-gray-200', accent: 'border-l-gray-300' };
};

const progressBarColor = (color) => {
  if (color === 'red') return 'bg-red-500';
  if (color === 'amber') return 'bg-amber-500';
  return 'bg-emerald-600';
};

// ---------------- secondary nav tabs ----------------
// (Now provided by shared component <SecondaryNav />)

// ---------------- main ----------------
export default function Home({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastProjects, setLastProjects] = useState(() => readLastProjects());

  const dismissOne = (projectId) => {
    const next = dismissProject(projectId);
    setLastProjects(next);
  };

  const dismissAll = () => {
    clearLastProjects();
    setLastProjects([]);
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'agora mesmo';
    if (minutes < 60) return `há ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `há ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'ontem';
    if (days < 7) return `há ${days} dias`;
    return then.toLocaleDateString('pt-BR');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/home/briefing');
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Erro ao carregar briefing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const greeting = useMemo(() => {
    const now = new Date();
    return greetingFor(now.getHours());
  }, []);

  const todayIso = data?.today || todayIsoBrt();

  return (
    <Layout user={user}>
      <div className="min-h-screen bg-gray-50 -m-6 lg:-m-8">
        {/* HERO BANNER */}
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 pt-8 pb-2">
            <h1 className="text-2xl md:text-3xl font-medium text-gray-900" data-testid="home-greeting">
              {greeting}, {data?.user?.first_name || user?.full_name?.split(' ')?.[0] || 'consultor'}. Aqui está o resumo de hoje.
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-testid="home-context-line">
              {formatLongDate()} · Curitiba, PR
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-testid="home-chips">
              <div className="flex items-center gap-2 text-gray-700">
                <FolderOpen className="w-4 h-4" style={{ color: GREEN }} />
                <span>
                  <span className="font-medium text-gray-900">{data?.chips?.active_projects ?? '—'}</span>{' '}
                  projetos ativos sob sua gestão
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <CheckSquare className="w-4 h-4" style={{ color: GREEN }} />
                <span>
                  <span className="font-medium text-gray-900">{data?.chips?.tasks_today ?? '—'}</span>{' '}
                  tarefas pendentes para hoje
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="w-4 h-4" style={{ color: GREEN }} />
                <span>
                  <span className="font-medium text-gray-900">{data?.chips?.deadlines_this_week ?? '—'}</span>{' '}
                  prazos vencem esta semana
                </span>
              </div>
            </div>
          </div>

          {/* Secondary tab nav */}
          <div className="max-w-[1400px] mx-auto">
            <SecondaryNav user={user} />
          </div>
        </section>

        {/* BODY */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 space-y-6">
          {loading && (
            <Card className="p-8 text-center text-gray-500">Carregando briefing...</Card>
          )}
          {error && (
            <Card className="p-6 text-red-600">{error}</Card>
          )}

          {data && (
            <>
              {/* CONTINUE WHERE LEFT OFF */}
              {lastProjects.length > 0 && (
                <Card
                  className="bg-white border border-emerald-200 rounded-xl p-4"
                  data-testid="home-continue-card"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: '#E1F5EE', color: GREEN }}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">Continuar de onde parou</div>
                        <div className="text-[11px] text-gray-500">
                          {lastProjects.length === 1
                            ? '1 projeto recente'
                            : `${lastProjects.length} projetos recentes`}
                        </div>
                      </div>
                    </div>
                    {lastProjects.length > 1 && (
                      <button
                        onClick={dismissAll}
                        className="text-[11px] text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-50 transition-colors"
                        data-testid="home-continue-dismiss-all"
                      >
                        Limpar todos
                      </button>
                    )}
                  </div>

                  <ul className="divide-y divide-gray-100">
                    {lastProjects.map((lp, idx) => (
                      <li
                        key={lp.project_id}
                        className="py-2.5 flex items-center gap-3"
                        data-testid={`home-continue-item-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div
                            onClick={() => navigate(`/projects/${lp.project_id}`)}
                            className="text-sm font-medium text-gray-900 hover:text-emerald-700 cursor-pointer truncate"
                            data-testid={`home-continue-name-${idx}`}
                          >
                            {lp.name || '(sem nome)'}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {lp.project_type ? `${lp.project_type} · ` : ''}
                            {formatRelative(lp.visited_at)}
                          </div>
                        </div>

                        <div className="hidden sm:flex items-center gap-2 shrink-0">
                          <div className="w-20 h-[3px] bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-600"
                              style={{ width: `${Math.min(100, lp.overall_percentage || 0)}%` }}
                            />
                          </div>
                          <span className="text-[11px] text-gray-600 w-9 text-right">
                            {(lp.overall_percentage || 0).toFixed(0)}%
                          </span>
                        </div>

                        <button
                          onClick={() => navigate(`/projects/${lp.project_id}`)}
                          className="text-xs text-emerald-700 hover:text-emerald-800 font-medium px-2.5 py-1 rounded hover:bg-emerald-50 transition-colors shrink-0"
                          data-testid={`home-continue-button-${idx}`}
                        >
                          Retomar →
                        </button>
                        <button
                          onClick={() => dismissOne(lp.project_id)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded shrink-0"
                          aria-label="Ignorar este projeto"
                          title="Ignorar"
                          data-testid={`home-continue-dismiss-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* WEEKLY TASKS */}
              <Card className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-medium text-gray-900">Minhas tarefas para a semana</h2>
                  <Link to="/analytics?tab=overview" className="text-sm text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid="home-tasks-see-all">
                    Ver todas <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {data.week_days.map((day) => (
                    <div key={day.date} className="flex flex-col gap-2 min-h-[160px]">
                      <div className={`text-xs font-medium px-3 py-2 rounded-md text-center ${
                        day.is_today
                          ? 'text-emerald-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      style={day.is_today ? { backgroundColor: '#E1F5EE' } : {}}
                      >
                        {day.label_short} · {day.date.slice(8, 10)}/{day.date.slice(5, 7)}
                      </div>
                      {day.tasks.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-4">Nenhuma tarefa</div>
                      ) : (
                        <>
                          {day.tasks.slice(0, 3).map((task) => {
                            const b = formatTaskBadge(task, todayIso);
                            return (
                              <div
                                key={task.task_id}
                                onClick={() => navigate(`/projects/${task.project_id}`)}
                                className={`bg-white border border-gray-200 border-l-4 ${b.accent} rounded-md p-2.5 cursor-pointer hover:shadow-sm transition-shadow`}
                                data-testid={`home-task-${task.task_id}`}
                              >
                                <div className="text-xs font-medium text-gray-900 leading-snug line-clamp-2">
                                  {task.name}
                                </div>
                                {task.project_name && (
                                  <div className="text-[11px] text-gray-500 mt-1 line-clamp-1">
                                    {task.project_name}
                                  </div>
                                )}
                                <Badge className={`mt-1.5 text-[10px] px-1.5 py-0 ${b.cls} border`}>
                                  {b.label}
                                </Badge>
                              </div>
                            );
                          })}
                          {day.tasks.length > 3 && (
                            <Link
                              to="/analytics?tab=overview"
                              className="text-[11px] text-emerald-700 hover:underline text-center pt-1"
                            >
                              + {day.tasks.length - 3} tarefas
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* BOTTOM 2-COL */}
              <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
                {/* Panorama */}
                <Card className="bg-white border border-gray-200 rounded-xl p-6" data-testid="home-panorama">
                  <h2 className="text-base font-medium text-gray-900 mb-4">Panorama geral</h2>
                  <div className="space-y-3">
                    <MetricRow
                      icon={<Folders className="w-4 h-4" />}
                      iconBg="bg-emerald-100 text-emerald-700"
                      label="Projetos ativos"
                      value={data.panorama.active_projects}
                    />
                    <MetricRow
                      icon={<CircleCheck className="w-4 h-4" />}
                      iconBg="bg-blue-100 text-blue-700"
                      label="Conclusão média"
                      value={`${data.panorama.avg_completion}%`}
                    />
                    <MetricRow
                      icon={<PauseCircle className="w-4 h-4" />}
                      iconBg="bg-amber-100 text-amber-700"
                      label="Em espera"
                      value={data.panorama.on_hold}
                    />
                    <MetricRow
                      icon={<AlertTriangle className="w-4 h-4" />}
                      iconBg="bg-red-100 text-red-700"
                      label="Atrasados"
                      value={data.panorama.late}
                    />
                  </div>
                </Card>

                {/* My projects in progress */}
                <Card className="bg-white border border-gray-200 rounded-xl p-6" data-testid="home-my-projects">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-medium text-gray-900">Meus projetos em andamento</h2>
                    <Link to="/projects" className="text-sm text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                      Ver todos <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                  {data.my_projects.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">Nenhum projeto em andamento.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {data.my_projects.map((p) => (
                        <li
                          key={p.project_id}
                          onClick={() => navigate(`/projects/${p.project_id}`)}
                          className="py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
                          data-testid={`home-project-${p.project_id}`}
                        >
                          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: '#E1F5EE', color: GREEN }}>
                            <Folders className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                            <div className="text-xs text-gray-500 truncate">{p.project_type || '—'}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="w-14 h-[3px] bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${progressBarColor(p.progress_color)} transition-all`}
                                style={{ width: `${Math.min(100, p.overall_percentage)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600 w-10 text-right">{p.overall_percentage}%</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {data.more_projects_count > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                      <Link to="/projects" className="text-sm text-emerald-700 hover:underline flex items-center justify-center gap-1">
                        Ver todos <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function MetricRow({ icon, iconBg, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
      <span className="flex-1 text-sm text-gray-700">{label}</span>
      <span className="text-lg font-medium text-gray-900">{value}</span>
    </div>
  );
}
